import moment from 'moment'
import SectionItem from '@/app/components/Layout/SectionItem/SectionItem'
import { LuxDatePicker, Color, LuxInputSwitch } from '@lux/wardrobe-lib'
import { useIntl } from 'react-intl'
import SectionItemContainer from '@/app/components/Layout/SectionItemContainer/SectionItemContainer'
import { FormBase, VisitSchedulingForm } from '@/app/models/new-activity-form'
import { VisitSurvey } from '@/app/models/new-activity-creation-state'
import { useEffect, useState } from 'react'
import './VisitScheduling.scss'
import { hasMysteryVisitDisabled } from '@Techedge/digitalportal-shared'

interface VisitSchedulingProps extends FormBase<VisitSchedulingForm> {
    language: string
    selectedSurveys: VisitSurvey[]
}

const VisitScheduling = ({
    form,
    language,
    selectedSurveys,
    onSetForm,
    onSave,
    onClear,
    isDone,
}: VisitSchedulingProps) => {
    const [isPickerStartVisible, setIsPickerStartVisible] = useState(false)
    const [isPickerStartTimeVisible, setIsPickerStartTimeVisible] = useState(false)
    const [isPickerEndTimeVisible, setIsPickerEndTimeVisible] = useState(false)

    const intl = useIntl()

    const componentClass = 'smart-new-activity-visit-scheduling'

    const onStartChangeCallback = (value) => {
        onSetForm({
            ...form,
            startDate: { ...form.startDate, value },
        })
        setIsPickerStartVisible(false)
    }
    const onStartTimeChangeCallback = (value) => {
        const surveysDuration = selectedSurveys.reduce((acc, curr) => {
            return acc + Number(curr.duration)
        }, 0)
        onSetForm({
            ...form,
            startTime: { ...form.startTime, value },
            endTime: {
                ...form.endTime,
                value:
                    surveysDuration < 60
                        ? new Date(moment(value).add(1, 'hours').format())
                        : new Date(moment(value).add(surveysDuration, 'minutes').format()),
            },
        })
    }
    const onEndTimeChangeCallback = (value) => {
        onSetForm({
            ...form,
            endTime: { ...form.endTime, value },
        })
    }

    const onFocus = () => {
        setIsPickerStartVisible(true)
    }
    const onFocusStartTime = () => {
        setIsPickerStartTimeVisible(true)
    }
    const onFocusEndTime = () => {
        setIsPickerEndTimeVisible(true)
    }

    const getStartTimeMinDate = () => {
        if (moment(new Date()).isSame(moment(form.startDate.value || new Date()), 'day'))
            return new Date()
    }

    useEffect(() => {
        if (!form.startTime.value) {
            onSetForm({
                ...form,
                startTime: {
                    ...form.startTime,
                    value: new Date(moment().add(10, 'minutes').format()),
                },
                endTime: {
                    ...form.endTime,
                    value: new Date(moment().add(10, 'minutes').add(1, 'hours').format()),
                },
            })
        }
    }, [])

    useEffect(() => {
        const surveysDuration = selectedSurveys.reduce((acc, curr) => {
            return acc + Number(curr.duration)
        }, 0)
        if (!!form.startTime.value) {
            onSetForm({
                ...form,
                endTime: {
                    ...form.endTime,
                    value:
                        surveysDuration > 60
                            ? new Date(
                                  moment(form.startTime.value)
                                      .add(surveysDuration, 'minutes')
                                      .format()
                              )
                            : new Date(moment(form.startTime.value).add(1, 'hours').format()),
                },
            })
        }
    }, [form.startTime.value, selectedSurveys])

    return (
        <div className={componentClass}>
            <SectionItemContainer
                hideFooter={true}
                onSave={() => onSave()}
                onClear={onClear}
                isDone={isDone}>
                {!hasMysteryVisitDisabled() && (
                    <SectionItem>
                        <LuxInputSwitch
                            onChangeCallback={() =>
                                onSetForm({
                                    ...form,
                                    mysteryVisit: { value: !form.mysteryVisit.value },
                                })
                            }
                            value={form.mysteryVisit.value}
                            title={intl.formatMessage({
                                id: 'modals.new.activity.section.scheduling.mystery.visit',
                                defaultMessage: 'Set mystery visit',
                            })}
                            label={intl.formatMessage({
                                id: `modals.new.activity.section.scheduling.mystery.visit.label`,
                                defaultMessage: 'Is Mystery Visit',
                            })}
                            additionalProps={{ disabled: form.mysteryVisit.disabled }}
                        />
                    </SectionItem>
                )}
                <SectionItem>
                    <LuxInputSwitch
                        onChangeCallback={() =>
                            onSetForm({
                                ...form,
                                remoteVisit: { value: !form.remoteVisit.value },
                            })
                        }
                        value={form.remoteVisit.value}
                        title={intl.formatMessage({
                            id: 'modals.new.activity.section.scheduling.remote.visit',
                            defaultMessage: 'Remote Visit',
                        })}
                        label={intl.formatMessage({
                            id: 'modals.new.activity.section.scheduling.remote.visit.label',
                            defaultMessage: 'Remote Visit',
                        })}
                        additionalProps={{ disabled: form.remoteVisit.disabled }}
                    />
                </SectionItem>
                <SectionItem>
                    <LuxDatePicker
                        intl={intl}
                        locale={language?.split('-')[0] || 'en'}
                        containerClasses={`${componentClass}__picker start`}
                        additionalProps={{
                            panelClassName: 'smart-wardrobe-datepicker scheduling-start-picker',
                            appendTo: 'self',
                        }}
                        value={form.startDate.value}
                        disabled={form.startDate.disabled}
                        onChangeCallback={onStartChangeCallback}
                        minDate={new Date()}
                        label={intl.formatMessage({
                            id: 'modals.new.activity.section.scheduling.set.visit.date',
                            defaultMessage: 'Set Visit Date',
                        })}
                        mandatory={true}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.section.scheduling.set.visit.date',
                            defaultMessage: 'Set Visit Date',
                        })}
                        showCustom
                        visible={isPickerStartVisible}
                        onFocus={onFocus}
                        style={{ backgroundColor: Color.WHITE }}
                        dateFormat={language?.split('-')[0] === 'tr' ? 'dd/mm/yy' : undefined}
                    />
                    <LuxDatePicker
                        intl={intl}
                        locale={language?.split('-')[0] || 'en'}
                        containerClasses={`${componentClass}__picker start-time`}
                        additionalProps={{
                            panelClassName:
                                'smart-wardrobe-datepicker scheduling-start-time-picker',
                            appendTo: 'self',
                            timeOnly: true,
                            onBlur: () => setIsPickerStartTimeVisible(false),
                            todayButtonClassName: 'today-button',
                            icon: 'pi pi-clock',
                        }}
                        value={form.startTime.value}
                        disabled={form.startTime.disabled}
                        onChangeCallback={onStartTimeChangeCallback}
                        mandatory={true}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.section.scheduling.visit.start.time',
                            defaultMessage: 'Start Time',
                        })}
                        visible={isPickerStartTimeVisible}
                        onFocus={onFocusStartTime}
                        onVisibleChange={() => {}}
                        style={{ backgroundColor: Color.WHITE }}
                        minDate={getStartTimeMinDate()}
                    />
                    <LuxDatePicker
                        intl={intl}
                        locale={language?.split('-')[0] || 'en'}
                        containerClasses={`${componentClass}__picker end-time`}
                        additionalProps={{
                            panelClassName: 'smart-wardrobe-datepicker scheduling-end-time-picker',
                            appendTo: 'self',
                            timeOnly: true,
                            onBlur: () => setIsPickerEndTimeVisible(false),
                            todayButtonClassName: 'today-button',
                            icon: 'pi pi-clock',
                        }}
                        value={form.endTime.value}
                        disabled={form.endTime.disabled}
                        onChangeCallback={onEndTimeChangeCallback}
                        mandatory={true}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.section.scheduling.visit.end.time',
                            defaultMessage: 'End Time',
                        })}
                        visible={isPickerEndTimeVisible}
                        onFocus={onFocusEndTime}
                        onVisibleChange={() => {}}
                        style={{ backgroundColor: Color.WHITE }}
                        minDate={form.startTime.value}
                    />
                </SectionItem>
                {/* <SectionItem>
                    <LuxInputSwitch
                        onChangeCallback={() =>
                            onSetForm({
                                ...form,
                                calendarVisibility: { value: !form.calendarVisibility.value },
                            })
                        }
                        value={form.calendarVisibility.value}
                        title={intl.formatMessage({
                            id: `modals.new.activity.section.scheduling.calendar.visibility`,
                            defaultMessage: 'Set calendar visibility',
                        })}
                        label={intl.formatMessage({
                            id: `modals.new.activity.section.scheduling.calendar.visibility.label`,
                            defaultMessage: 'Task Visible in Calendar',
                        })}
                        additionalProps={{ disabled: form.calendarVisibility.disabled }}
                    />
                </SectionItem> */}
            </SectionItemContainer>
        </div>
    )
}

export default VisitScheduling
